/**
 * Seam between the category-image UI and the backend that has to serve it.
 *
 * `menu_core_be` has no category images at all today: `categories.controller.ts`
 * exposes only the CRUD routes, the `Category` entity has no image relation,
 * `storage-paths.ts` has no `CATEGORY_IMAGE`, and `permission.enum.ts` has no
 * `*_CATEGORY_IMAGES`. So the whole RMS side is written and wired, and this flag
 * keeps it out of the form until the endpoints exist, an image picker that
 * accepts a photo and silently drops it is worse than no picker.
 *
 * Flip to `true` once the backend below is live. Nothing else needs changing.
 *
 * Required contract, mirroring dishes so the two stay one pattern:
 *
 *  POST   /restaurants/{restaurantId}/categories/{categoryId}/images
 *         multipart/form-data, single field `file` → 201, empty body.
 *         Replaces any existing image, as `DishesRepository.saveImage` does
 *         (delete the row, then save), the UI offers no "replace" affordance.
 *
 *  GET    /restaurants/{restaurantId}/categories/{categoryId}/images
 *         → 200 `{ id, url, version }`, 404 when the category has none.
 *         Note `url`, not `imageUrl`: `DishImageDto` is the shape to copy.
 *         404 is the normal empty case here, so it must not be an error state.
 *
 *  DELETE /restaurants/{restaurantId}/categories/{categoryId}/images → 200.
 *
 *  GET    /restaurants/{restaurantId}/categories
 *         each category gains `image?: { id, url, version }`, exactly as
 *         `DishDto.image` does, so the sidebar can show a thumbnail without a
 *         request per row.
 *
 *  POST   /restaurants/{restaurantId}/categories
 *         must answer with the created id, `{ id }`, as POST /dishes does.
 *         It currently returns `{ message } | void`, and without an id a photo
 *         picked while creating a category has nowhere to go: the image route
 *         is keyed by a category that does not exist yet at submit time.
 */
export const CATEGORY_IMAGES_ENABLED = false

/**
 * Seam for the hours a menu type is served.
 *
 * A breakfast menu that runs 08:00–12:00 should only be offered in the waiter
 * and guest apps inside that window; no range at all means it is served all day.
 *
 * `menu_core_be` cannot store this today: the `MenuType` entity has only
 * `menuTypeId`, `restaurantId` and its timestamps, and `CreateMenuTypeDto` /
 * `UpdateMenuTypeDto` carry only `translations` and `categoryIds`. So the RMS
 * side is written and this flag keeps it out of the form until it can be saved.
 *
 * Flip to `true` once the backend below is live. Nothing else needs changing.
 *
 * Required contract:
 *
 *  Create/UpdateMenuTypeDto gain two optional fields:
 *      activeFrom?: string | null   // "HH:mm", 24-hour
 *      activeTo?:   string | null   // "HH:mm", 24-hour
 *    Both null (or both absent) means served all day, that is the default for
 *    every menu type that exists today, so the migration needs no backfill.
 *    They travel as a pair: one set without the other is rejected here before
 *    the request is made, and the backend should reject it too.
 *
 *  GET /menu-types/{id} returns the same two fields, that is the one the RMS
 *  reads, to fill the edit form. GET /menu-types should carry them too, but for
 *  the waiter and guest apps rather than for this one: the sidebar list does
 *  not display hours, so nothing here would notice if they were missing.
 *
 *  The wire format is "HH:mm" and nothing else. A Postgres `time` column
 *  serialises as "08:00:00" by default, and the form re-sends whatever it was
 *  given, so trim the seconds server-side, or the stored value drifts a format
 *  every time somebody opens the menu type and presses save.
 *
 *  `activeTo` earlier than or equal to `activeFrom` means the window crosses
 *  midnight (a late menu of 22:00–02:00), which is why this is not validated as
 *  "end after start". Whoever consumes it has to compare accordingly.
 *
 *  Storing them as `time` columns is enough; there is no date part and no time
 *  zone, the restaurant's own clock is the only frame of reference.
 */
// Turned on so the field is usable now. Until the backend below exists the
// picked hours are NOT persisted: the API strips unknown properties, so the
// pair is dropped on save and the form comes back empty after a reload.
// Nothing else breaks, no existing data is touched, and flipping this back to
// `false` hides the field again with no trace.
export const MENU_TYPE_HOURS_ENABLED = true
